import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Grid, Chip, IconButton, 
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  FormControl, InputLabel, Select, MenuItem, Divider, Avatar, List, ListItem,
  ListItemText, ListItemAvatar, Accordion, AccordionSummary, AccordionDetails,
  CircularProgress, Alert, useTheme, useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Person as PersonIcon,
  Phone as PhoneIcon, Email as EmailIcon, Home as HomeIcon, Security as SecurityIcon,
  Emergency as EmergencyIcon, Assignment as AssignmentIcon, ExpandMore as ExpandMoreIcon,
  AttachMoney as MoneyIcon, CalendarToday as CalendarIcon, Description as DescriptionIcon,
  Mail as MailIcon
} from '@mui/icons-material';
import api from '../api';
import EntityDocumentsStrip from './EntityDocumentsStrip';
import { isFinancialsEnabled } from '../config/featureFlags';
import { stashLedgerNav } from './financials/api';


const TenantManagement = ({ onGoToFinancials, onOpenDocuments }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const finEnabled = isFinancialsEnabled();
  
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addTenantDialog, setAddTenantDialog] = useState(false);
  const [editTenantDialog, setEditTenantDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [mounted, setMounted] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [invitationLinkDialog, setInvitationLinkDialog] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');

  const [newTenant, setNewTenant] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    driversLicense: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    securityDeposit: {
      amount: '',
      received: false,
      receivedDate: '',
      receiptNumber: ''
    },
    leaseId: '',
    employment: {
      employer: '',
      position: '',
      phone: '',
      income: ''
    },
    notes: ''
  });

  useEffect(() => {
    setMounted(true);
    fetchTenants(true); // Show loading on initial fetch
    fetchProperties();
    fetchLeases();
  }, []);

  const fetchTenants = async (showLoading = false) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      console.log('🔍 Fetching tenants...');
      const response = await api.get('/api/tenants');
      console.log('✅ Tenants fetched successfully:', response.data);
      setTenants(response.data);
      console.log('✅ Tenants state updated');
    } catch (error) {
      console.error('❌ Error fetching tenants:', error);
      setSnackbar({ open: true, message: 'Failed to fetch tenants', severity: 'error' });
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await api.get('/api/properties');
      setProperties(response.data);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchLeases = async () => {
    try {
      console.log('🔍 Fetching leases...');
      const response = await api.get('/api/leases');
      console.log('✅ Leases fetched successfully:', response.data);
      setLeases(response.data);
    } catch (error) {
      console.error('❌ Failed to fetch leases:', error);
    }
  };

  const handleAddTenant = async () => {
    try {
      await api.post('/api/tenants', newTenant);
      setSnackbar({ open: true, message: 'Tenant added successfully!', severity: 'success' });
      setAddTenantDialog(false);
      resetNewTenant();
      fetchTenants(false); // Don't show loading spinner for refresh
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to add tenant', severity: 'error' });
    }
  };

  const handleEditTenant = async () => {
    if (updating) return; // Prevent double submission
    
    try {
      setUpdating(true);
      console.log('🔍 Updating tenant:', selectedTenant.id);
      console.log('🔍 Tenant data:', selectedTenant);
      
      const response = await api.put(`/api/tenants/${selectedTenant.id}`, selectedTenant);
      console.log('✅ Update response:', response.data);
      
      // Close dialog and clear selected tenant first
      setEditTenantDialog(false);
      setSelectedTenant(null);
      
      // Show success message
      setSnackbar({ open: true, message: 'Tenant updated successfully!', severity: 'success' });
      
      // Refresh tenants data after a short delay to prevent race conditions
      setTimeout(() => {
        console.log('🔄 Refreshing tenants after update...');
        fetchTenants(false); // Don't show loading spinner for refresh
      }, 100);
      
    } catch (error) {
      console.error('❌ Update error:', error);
      console.error('❌ Error response:', error.response?.data);
      setSnackbar({ 
        open: true, 
        message: `Failed to update tenant: ${error.response?.data?.error || error.message}`, 
        severity: 'error' 
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm('Are you sure you want to delete this tenant?')) return;
    try {
      await api.delete(`/api/tenants/${tenantId}`);
      setSnackbar({ open: true, message: 'Tenant deleted successfully!', severity: 'success' });
      fetchTenants(false); // Don't show loading spinner for refresh
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete tenant', severity: 'error' });
    }
  };

  const handleSendInvitation = async (tenant) => {
    try {
      if (!tenant.email) {
        setSnackbar({ open: true, message: 'Tenant does not have an email address', severity: 'error' });
        return;
      }

      console.log('📧 Sending invitation to:', tenant.email);
      
      const response = await api.post('/api/tenant-invitations/send-invitation', {
        tenantId: tenant.id
      });
      
      console.log('✅ Invitation response:', response.data);
      
      // Check if email was actually sent
      if (response.data.emailSent === false) {
        // Email service not configured - show activation link
        setInvitationLink(response.data.activationLink);
        setInvitationLinkDialog(true);
        setSnackbar({ 
          open: true, 
          message: 'Email service not configured. Share the activation link manually.', 
          severity: 'warning' 
        });
      } else {
        // Email sent successfully
        setSnackbar({ 
          open: true, 
          message: `Portal invitation sent to ${tenant.email}!`, 
          severity: 'success' 
        });
      }
    } catch (error) {
      console.error('❌ Error sending invitation:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.error || 'Failed to send invitation', 
        severity: 'error' 
      });
    }
  };

  const resetNewTenant = () => {
    setNewTenant({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      driversLicense: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
        email: ''
      },
      securityDeposit: {
        amount: '',
        received: false,
        receivedDate: '',
        receiptNumber: ''
      },
      leaseId: '',
      employment: {
        employer: '',
        position: '',
        phone: '',
        income: ''
      },
      notes: ''
    });
  };

  const getPropertyName = (tenant) => {
    const lease = leases.find(l => l.id === tenant.leaseId);
    if (!lease || !lease.propertyId) return 'Unknown Property';
    const property = properties.find(p => p.id === lease.propertyId);
    return property ? property.name : 'Unknown Property';
  };

  const getUnitName = (tenant) => {
    const lease = leases.find(l => l.id === tenant.leaseId);
    if (!lease || !lease.propertyId || !lease.unitId) return 'Unknown Unit';
    const property = properties.find(p => p.id === lease.propertyId);
    if (property && property.units) {
      const unit = property.units.find(u => u.id === lease.unitId);
      return unit ? unit.name : 'Unknown Unit';
    }
    return 'Unknown Unit';
  };

  const getTenantStatus = (tenant) => {
    // Check if tenant has a lease and if it's active
    const lease = leases.find(l => l.id === tenant.leaseId);
    if (!lease || !lease.endDate) return 'Active';
    const endDate = new Date(lease.endDate);
    const today = new Date();
    return endDate > today ? 'Active' : 'Expired';
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><Typography>Loading tenants...</Typography></Box>;
  }

  // Add error boundary for debugging
  try {
    console.log('🔍 Component render - mounted:', mounted, 'loading:', loading, 'tenants count:', tenants.length);

    if (!mounted) {
      console.log('❌ Component not mounted, returning null');
      return null;
    }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <PersonIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Tenant Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddTenantDialog(true)}
        >
          Add Tenant
        </Button>
      </Box>

      {/* Tenant Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="primary"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Total Tenants
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {tenants.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="success.main"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Active Leases
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                {tenants.filter(t => getTenantStatus(t) === 'Active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="warning.main"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Security Deposits
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                ${tenants.reduce((sum, t) => sum + (parseFloat(t.securityDeposit?.amount) || 0), 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Typography 
                variant="h6" 
                color="info.main"
                sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' } }}
              >
                Monthly Rent
              </Typography>
              <Typography 
                variant="h4"
                sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
              >
                ${tenants.reduce((sum, t) => {
                  const lease = leases.find(l => l.id === t.leaseId);
                  return sum + (parseFloat(lease?.monthlyRent) || 0);
                }, 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tenants List */}
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            All Tenants
          </Typography>
          <TableContainer 
            component={Paper}
            sx={{ 
              overflowX: 'auto',
              maxWidth: { xs: '100%', sm: 'none' }
            }}
          >
            <Table sx={{ minWidth: { xs: 600, sm: 'auto' } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Tenant</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Contact</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Property/Unit</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Lease Status</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Security Deposit</TableCell>
                  <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow key={tenant.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 } }}>
                          {tenant.firstName?.charAt(0)}{tenant.lastName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography 
                            variant="subtitle2"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            {tenant.firstName} {tenant.lastName}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            color="textSecondary"
                            sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          >
                            License: {tenant.driversLicense}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          <PhoneIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                          {tenant.phone}
                        </Typography>
                        <Typography 
                          variant="body2"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          <EmailIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5, verticalAlign: 'middle' }} />
                          {tenant.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {getPropertyName(tenant)}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="textSecondary"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      >
                        {getUnitName(tenant)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={getTenantStatus(tenant)} 
                        color={getTenantStatus(tenant) === 'Active' ? 'success' : 'error'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        ${parseFloat(tenant.securityDeposit?.amount || 0).toFixed(2)}
                      </Typography>
                      <Chip 
                        label={tenant.securityDeposit?.received ? 'Received' : 'Pending'} 
                        color={tenant.securityDeposit?.received ? 'success' : 'warning'} 
                        size="small"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 }, flexWrap: 'wrap' }}>
                        {finEnabled && (
                          <Tooltip title="View rent ledger" disablePortal>
                            <Button
                              size="small"
                              onClick={() => {
                                const propertyId =
                                  tenant.propertyId ||
                                  tenant.leaseInfo?.propertyId ||
                                  leases.find((l) => l.tenantId === tenant.id)?.propertyId;
                                const unitId =
                                  tenant.unitId ||
                                  tenant.leaseInfo?.unitId ||
                                  leases.find((l) => l.tenantId === tenant.id)?.unitId;
                                const leaseId =
                                  tenant.leaseId ||
                                  leases.find((l) => l.tenantId === tenant.id)?.id;
                                if (!propertyId) {
                                  setSnackbar({
                                    open: true,
                                    message: 'Tenant needs a property to open ledger',
                                    severity: 'warning'
                                  });
                                  return;
                                }
                                stashLedgerNav({
                                  tenantId: tenant.id,
                                  propertyId,
                                  unitId,
                                  leaseId,
                                  tenantLabel: `${tenant.firstName || ''} ${tenant.lastName || ''}`.trim()
                                });
                                if (typeof onGoToFinancials === 'function') onGoToFinancials();
                              }}
                            >
                              Ledger
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip 
                          title="Edit Tenant"
                          TransitionComponent={undefined}
                          disablePortal={true}
                        >
                          <IconButton 
                            size={isMobile ? 'small' : 'medium'} 
                            onClick={() => {
                              setSelectedTenant(tenant);
                              setEditTenantDialog(true);
                            }}
                          >
                            <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip 
                          title="Delete Tenant"
                          TransitionComponent={undefined}
                          disablePortal={true}
                        >
                          <IconButton 
                            size={isMobile ? 'small' : 'medium'} 
                            color="error"
                            onClick={() => handleDeleteTenant(tenant.id)}
                          >
                            <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip 
                          title="Send Portal Invitation"
                          TransitionComponent={undefined}
                          disablePortal={true}
                        >
                          <IconButton 
                            size={isMobile ? 'small' : 'medium'} 
                            color="primary"
                            onClick={() => handleSendInvitation(tenant)}
                          >
                            <MailIcon fontSize={isMobile ? 'small' : 'medium'} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add Tenant Dialog */}
      <Dialog 
        open={addTenantDialog} 
        onClose={() => setAddTenantDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={undefined}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Add New Tenant
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {addTenantDialog && <TenantForm tenant={newTenant} setTenant={setNewTenant} properties={properties} leases={leases} />}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setAddTenantDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAddTenant} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Add' : 'Add Tenant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog 
        open={editTenantDialog} 
        onClose={() => setEditTenantDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={undefined}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Tenant
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          {editTenantDialog && selectedTenant && (
            <>
              <TenantForm tenant={selectedTenant} setTenant={setSelectedTenant} properties={properties} leases={leases} />
              <EntityDocumentsStrip
                tenantId={selectedTenant.id}
                propertyId={
                  leases.find((l) => l.tenantId === selectedTenant.id)?.propertyId ||
                  selectedTenant.leaseInfo?.propertyId ||
                  ''
                }
                entityLabel={[selectedTenant.firstName, selectedTenant.lastName].filter(Boolean).join(' ') || 'Tenant'}
                onOpenInHub={
                  typeof onOpenDocuments === 'function'
                    ? () => {
                        setEditTenantDialog(false);
                        onOpenDocuments(selectedTenant.id);
                      }
                    : null
                }
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditTenantDialog(false)} 
            disabled={updating}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditTenant} 
            variant="contained" 
            disabled={updating}
            startIcon={updating ? <CircularProgress size={20} /> : null}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {updating ? 'Updating...' : (isMobile ? 'Update' : 'Update Tenant')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invitation Link Dialog */}
      <Dialog 
        open={invitationLinkDialog} 
        onClose={() => setInvitationLinkDialog(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Portal Invitation Link
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Email service is not configured.</strong> The invitation was created but the email was not sent automatically.
            </Typography>
          </Alert>
          <Typography variant="body2" gutterBottom>
            Please share this activation link with the tenant manually (via text, email, etc.):
          </Typography>
          <TextField
            fullWidth
            value={invitationLink}
            margin="normal"
            multiline
            rows={3}
            InputProps={{
              readOnly: true,
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 1 }, 
            mt: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(invitationLink);
                setSnackbar({ open: true, message: 'Link copied to clipboard!', severity: 'success' });
              }}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'medium'}
            >
              Copy Link
            </Button>
            <Button
              variant="outlined"
              onClick={() => window.open(`mailto:?subject=Tenant Portal Invitation&body=${encodeURIComponent('Click this link to activate your tenant portal account: ' + invitationLink)}`)}
              fullWidth={isMobile}
              size={isMobile ? 'medium' : 'medium'}
            >
              {isMobile ? 'Email' : 'Send via Email'}
            </Button>
          </Box>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            display="block" 
            sx={{ mt: { xs: 1.5, sm: 2 }, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
          >
            This link will expire in 7 days. The tenant can use it to set their password and activate their account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setInvitationLinkDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        TransitionComponent={undefined}
        disablePortal={true}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
  } catch (error) {
    console.error('❌ Component render error:', error);
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error" variant="h6">
          Error loading tenant management
        </Typography>
        <Typography color="error">
          {error.message}
        </Typography>
      </Box>
    );
  }
};

const TenantForm = ({ tenant, setTenant, properties = [], leases = [] }) => {
  const updateTenant = (field, value) => {
    setTenant(prev => ({ ...prev, [field]: value }));
  };

  const updateNestedField = (section, field, value) => {
    setTenant(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Personal Information */}
        <Grid item xs={12}>
          <Accordion 
            defaultExpanded 
            TransitionComponent={undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Personal Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First Name"
                    value={tenant.firstName || ''}
                    onChange={(e) => updateTenant('firstName', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last Name"
                    value={tenant.lastName || ''}
                    onChange={(e) => updateTenant('lastName', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email"
                    type="email"
                    value={tenant.email || ''}
                    onChange={(e) => updateTenant('email', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Phone"
                    value={tenant.phone || ''}
                    onChange={(e) => updateTenant('phone', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Date of Birth"
                    type="date"
                    value={tenant.dateOfBirth || ''}
                    onChange={(e) => updateTenant('dateOfBirth', e.target.value)}
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Driver's License Number"
                    value={tenant.driversLicense || ''}
                    onChange={(e) => updateTenant('driversLicense', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Emergency Contact */}
        <Grid item xs={12}>
          <Accordion
            TransitionComponent={undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <EmergencyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Emergency Contact
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Emergency Contact Name"
                    value={tenant.emergencyContact?.name || ''}
                    onChange={(e) => updateNestedField('emergencyContact', 'name', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Relationship"
                    value={tenant.emergencyContact?.relationship || ''}
                    onChange={(e) => updateNestedField('emergencyContact', 'relationship', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Emergency Contact Phone"
                    value={tenant.emergencyContact?.phone || ''}
                    onChange={(e) => updateNestedField('emergencyContact', 'phone', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Emergency Contact Email"
                    type="email"
                    value={tenant.emergencyContact?.email || ''}
                    onChange={(e) => updateNestedField('emergencyContact', 'email', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Security Deposit */}
        <Grid item xs={12}>
          <Accordion
            TransitionComponent={undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Security Deposit
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Security Deposit Amount"
                    type="number"
                    value={tenant.securityDeposit?.amount || ''}
                    onChange={(e) => updateNestedField('securityDeposit', 'amount', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Deposit Received</InputLabel>
                    <Select
                      value={tenant.securityDeposit?.received || false}
                      onChange={(e) => updateNestedField('securityDeposit', 'received', e.target.value)}
                      MenuProps={{
                        TransitionComponent: undefined
                      }}
                    >
                      <MenuItem value={true}>Yes</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Received Date"
                    type="date"
                    value={tenant.securityDeposit?.receivedDate || ''}
                    onChange={(e) => updateNestedField('securityDeposit', 'receivedDate', e.target.value)}
                    fullWidth
                    margin="dense"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Receipt Number"
                    value={tenant.securityDeposit?.receiptNumber || ''}
                    onChange={(e) => updateNestedField('securityDeposit', 'receiptNumber', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Lease Assignment */}
        <Grid item xs={12}>
          <Accordion
            TransitionComponent={undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Lease Assignment
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth margin="dense">
                    <InputLabel>Select Lease</InputLabel>
                    <Select
                      value={tenant.leaseId || ''}
                      onChange={(e) => updateTenant('leaseId', e.target.value)}
                      MenuProps={{
                        TransitionComponent: undefined
                      }}
                    >
                      <MenuItem value="">
                        <em>No lease assigned</em>
                      </MenuItem>
                      {leases.filter(lease => lease.status === 'active').map((lease) => {
                        const property = properties.find(p => p.id === lease.propertyId);
                        const unit = property?.units?.find(u => u.id === lease.unitId);
                        return (
                          <MenuItem key={lease.id} value={lease.id}>
                            {property?.name || 'Unknown Property'} - {unit?.name || 'Unknown Unit'} 
                            {' '}(${lease.monthlyRent}/month)
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                    💡 To create a new lease, use the Lease Management screen
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Employment Information */}
        <Grid item xs={12}>
          <Accordion
            TransitionComponent={undefined}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Employment Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Employer"
                    value={tenant.employment?.employer || ''}
                    onChange={(e) => updateNestedField('employment', 'employer', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Position"
                    value={tenant.employment?.position || ''}
                    onChange={(e) => updateNestedField('employment', 'position', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Work Phone"
                    value={tenant.employment?.phone || ''}
                    onChange={(e) => updateNestedField('employment', 'phone', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Monthly Income"
                    type="number"
                    value={tenant.employment?.income || ''}
                    onChange={(e) => updateNestedField('employment', 'income', e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            label="Additional Notes"
            value={tenant.notes || ''}
            onChange={(e) => updateTenant('notes', e.target.value)}
            fullWidth
            multiline
            rows={4}
            margin="dense"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default TenantManagement; 